import React, { useEffect, useRef, useState } from "react";
import { Table, Input, message, Tabs, ConfigProvider, Spin, Button, Space, Modal, Form, Row, Col, Drawer } from "antd";
import { EditOutlined, CloseOutlined } from "@ant-design/icons";
import { isNotNullUndefined, compareObjects } from "../helpers/common";
import { storage } from "../utils/storage";
import { useNavigate } from "react-router-dom";
import { useFetchReferCodeUser, useReferCodeUpdate } from "../services/refercode.service";
import { IReferCode } from "../types/refercode";
import { eventEmitter } from "../helpers/eventemitter";
import Pagination from "../helpers/pagination";
import { useAuth } from "../context/AuthContext";
const ReferCode: React.FC = () => {
    const { Search } = Input;
    const { Column } = Table;
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [loading, setIsLoading] = useState(false);
    const { TabPane } = Tabs;
    const pageNumber = useRef(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const searchValue = useRef<string>(""); 
    const [referCodeList, setReferCodeList] = useState<IReferCode[]>([]);
    const [open, setOpen] = useState(false);
    const [editingRecord, setEditingRecord] = useState<IReferCode | null>(null);
    const [isConfirmModalVisible, setIsConfirmModalVisible] = useState(false);
    const [formUpdateValues, setFormUpdateValues] = useState<any>({});
    const [drawerLoading, setDrawerLoading] = useState(false);
    const formInitialValues = useRef<any>({});
    const formUpdatedValues = useRef<any>({});
    
    const [activeTab, setActiveTab] = useState('Agent');
    const tabStatus = [{ id: 0, name: "Agent" }, { id: 1, name: "Traveller" }];
      const { hasPermission, permissionsLoaded, loadPermissions } = useAuth();

    useEffect(() => {
            loadPermissions();
    }, [permissionsLoaded]);

     const ReferralCodeEdit = hasPermission("REFERRAL_CODE", "UPDATE");
    //  //#region on-load useEffect operations
    useEffect(() => {
        let userRoleId = parseInt(storage.getUserRoleId() ?? "0");
         if (![1, 4].includes(userRoleId)) {
            navigate("/");
        } else {
            setIsLoading(true);
            fetchReferCodeUserList(activeTab,pageNumber.current, pageSize, searchValue.current ?? "");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, pageSize]);

    // Initialize update mutation
    const updateReferCodeMutation = useReferCodeUpdate({
        onSuccess: (response: any) => {
            if (response.statusCode === 200) {
                message.success("Referral code has been updated successfully!");
                setDrawerLoading(false);
                setIsConfirmModalVisible(false);
                onClose();
                fetchReferCodeUserList(activeTab, pageNumber.current, pageSize, searchValue.current ?? "");
            } else {
                message.error(response.statusMessage || 'Failed to update referral code');
                setDrawerLoading(false);
            }
        },
        onError: (error: any) => {
            message.error(error.message || 'Failed to update referral code');
            setDrawerLoading(false);
        }
    });

    // Edit drawer functions
    const showDrawer = (record: IReferCode) => {
        setEditingRecord(record);
        form.setFieldsValue({
            fullName: record.fullName,
            email: record.email,
            referralCode: record.referralCode,
        });
        formInitialValues.current = { referralCode: record.referralCode };
        eventEmitter.emit("clickableChange", true);
        setOpen(true);
    };

    const onClose = () => {
        eventEmitter.emit("clickableChange", false);
        form.resetFields();
        setOpen(false);
        setEditingRecord(null);
    };

    const onFinish = (values: any) => {
        formUpdatedValues.current = { referralCode: values.referralCode };
        
        // Check if there are any changes
        if (compareObjects(formInitialValues.current, formUpdatedValues.current)) {
            onClose();
            return;
        }
        
        setIsConfirmModalVisible(true);
        setFormUpdateValues(values);
    };

    const handleSave = async () => {
        try {
            setDrawerLoading(true);
            
            const cleanedReferralCode = (formUpdateValues.referralCode || '')
            .replace(/\s+/g, '')
            .toUpperCase();
         
            updateReferCodeMutation.mutate({   
                fullName: editingRecord?.fullName,
                email: editingRecord?.email,
                referralID: editingRecord?.referralID,
                referralCode: cleanedReferralCode
            });
            
        } catch (error) {
            setDrawerLoading(false);
            message.error('Failed to update referral code');
        }
    };

    const handleCancel = () => {
        setIsConfirmModalVisible(false);
    };

    const onSearch = (value: any) => {
        pageNumber.current = 1;
        setPageSize(10);
        searchValue.current = value;
        fetchReferCodeUserList(activeTab, pageNumber.current, pageSize, searchValue.current ?? "");  
    };
    
    const tabClicked = (key: string) => {
        const tabsName = key === "0" ? "Agent" : "Traveller";
        setActiveTab(tabsName);
        pageNumber.current = 1; // Reset to first page on tab change
        fetchReferCodeUserList(tabsName, pageNumber.current, pageSize, searchValue.current ?? "");
        eventEmitter.emit("clickableChange", false);
    };
    
    const handlePageChange = (newPage: number) => {
        pageNumber.current = newPage;
        let size = pageSize;
        setPageSize(size);
        fetchReferCodeUserList(activeTab,newPage, pageSize, searchValue.current ?? "");
    };



    const fetchReferCodeUserList = async (tabName: string,  pageNumber: number, pageSize: number,searchFilter: string) => {
        setIsLoading(true);
        setReferCodeList([]);
        fetchReferCodeUserMutation.mutate({ tabName: tabName, searchFilter: searchFilter, pageNumber: pageNumber, pageSize: pageSize  });
    };

    const fetchReferCodeUserMutation = useFetchReferCodeUser({
        onSuccess: (response: any) => {
            if (response.statusCode === 200) {
                if (isNotNullUndefined(response.data) && response.data.length > 0) {
                    const formattedData = response.data.map((data: any) => ({
                        ...data
                    }));
                    setReferCodeList(formattedData);
                    const recordCount = formattedData[0]?.recordCount;
                    setTotalPages(Math.ceil(recordCount / pageSize));
                    setIsLoading(false);
                } else {
                    setReferCodeList([]);
                    setIsLoading(false);
                }
            } else {
                message.error(response.statusMessage);
                setIsLoading(false);
            }
        },
        onError: (error: any) => {
            message.error(error.message);
            setIsLoading(false);
        }
    });

    return (
        <div>
            <div className="flex justify-between align-middle pb-5">
                <h2 className="text-2xl font-semibold text-black">ReferralCode Panel</h2>
                <div className="w-80 max-w-80">
                    <Search
                        placeholder="Search Referral"
                        size="large"
                        enterButton
                        className="search-field"
                        onSearch={onSearch}
                    />
                </div>
            </div>

            {/* Edit Drawer */}
            <Drawer
                title="Edit Referral Code"
                width={370}
                onClose={onClose}
                loading={drawerLoading}
                mask={false}
                open={open}
            >
                <Form layout="vertical" form={form} onFinish={onFinish}>
                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item
                                name="fullName"
                                label="Full Name"
                            >
                                <Input size="large" disabled />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item
                                name="email"
                                label="Email"
                            >
                                <Input size="large" disabled />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item
                            name="referralCode"
                            label="Referral Code"
                            rules={[
                                { required: true, message: 'Please enter referral code' },
                                {
                                validator: (_, value) => {
                                    if (!value) {
                                    return Promise.resolve(); // required rule will handle empty
                                    }

                                    if (value.length < 6 || value.length > 12) {
                                    return Promise.reject(
                                        new Error('Referral code must be between 6 and 12 characters')
                                    );
                                    }

                                    if (!/\d/.test(value)) {
                                    return Promise.reject(
                                        new Error('Referral code must contain at least one number')
                                    );
                                    }

                                    return Promise.resolve();
                                },
                                },
                            ]}
                            >
                            <Input size="large" placeholder="Enter referral code" />
                            </Form.Item>
                        </Col>
                        </Row>

                    <Space className="w-full justify-end">
                        <Button onClick={onClose} className="hover:!text-black hover:!border-black">
                            Cancel
                        </Button>
                        <Button 
                            htmlType="submit" 
                            className="bg-black text-white hover:bg-transparent hover:!text-black hover:!border-black"
                        >
                            Update
                        </Button>
                    </Space>
                </Form>
            </Drawer>

            {/* Confirmation Modal */}
            <Modal
                title={<span className="custom-modal-title">Confirm Update!</span>}
                open={isConfirmModalVisible}
                onOk={handleSave}
                onCancel={handleCancel}
                closeIcon={<CloseOutlined className="text-gray-500 hover:text-black" />}
                footer={[
                    <Button key="back" onClick={handleCancel} className="hover:!text-black hover:!border-black">
                        Cancel
                    </Button>,
                    <Button key="submit" onClick={handleSave} className="bg-black text-white hover:bg-transparent hover:!text-black hover:!border-black">
                        Confirm
                    </Button>,
                ]}
            >
                <p>Are you sure you want to update the referral code to <b>{formUpdateValues.referralCode}</b>? Once updated, this referral code will be changed accordingly. Do you wish to proceed?</p>
            </Modal>

            <ConfigProvider
                theme={{
                    components: {
                        Tabs: {
                            titleFontSizeLG: 16,
                        },
                    }
                }}
            >
                <Tabs defaultActiveKey="0" onTabClick={tabClicked}>
                    {
                        tabStatus.map((x: any) => (
                            <TabPane tab={`${x.name}`} key={`${x.id}`}>
                                <Table dataSource={referCodeList} className="userlist-tabs-table" bordered  pagination={{ pageSize: pageSize, position: ['none'] }} locale={{
                                    emptyText: loading ? <Spin /> : (
                                        <div className="ant-empty">
                                            <div className="ant-empty-image">
                                                <svg width="64" height="41" viewBox="0 0 64 41" xmlns="http://www.w3.org/2000/svg">
                                                    <g transform="translate(0 1)" fill="none" fillRule="evenodd">
                                                        <ellipse fill="#f5f5f5" cx="32" cy="33" rx="32" ry="7"></ellipse>
                                                        <g fillRule="nonzero" stroke="#d9d9d9">
                                                            <path d="M55 12.76L44.854 1.258C44.367.474 43.656 0 42.907 0H21.093c-.749 0-1.46.474-1.947 1.257L9 12.761V22h46v-9.24z"></path>
                                                            <path d="M41.613 15.931c0-1.605.994-2.93 2.227-2.931H55v18.137C55 33.26 53.68 35 52.05 35h-40.1C10.32 35 9 33.259 9 31.137V13h11.16c1.233 0 2.227 1.323 2.227 2.928v.022c0 1.605 1.005 2.901 2.237 2.901h14.752c1.232 0 2.237-1.308 2.237-2.913v-.007z" fill="#fafafa"></path>
                                                        </g>
                                                    </g>
                                                </svg>
                                            </div>
                                            <div className="ant-empty-description">No data</div>
                                        </div>
                                    )
                                }}
                                >
                                    {
                                        activeTab === "Agent" ? (
                                            <Column title="User Name" dataIndex="fullName" key="fullName" align="center" width={200} />
                                        ) : (
                                            <Column title="User Name" dataIndex="fullName" key="fullName" align="center" width={200} />
                                        )
                                    }
                                    {
                                        activeTab === "Agent" ? (
                                            <Column title="User Email" dataIndex="email" key="email" align="center" width={150} />
                                        ) : (
                                            <Column title="User Email" dataIndex="email" key="email" width={200} />
                                        )
                                    }
                                    {
                                        activeTab === "Agent" ? (
                                            <Column title="Referral Code" dataIndex="referralCode" key="referralCode" align="center" width={200} />
                                        ) : (
                                            <Column title="Referral Code" dataIndex="referralCode" key="referralCode" align="center" width={150} />
                                        )
                                    }
                            
                                    <Column width={150}
                                        align="center"
                                        title="Action"
                                        key="action"
                                        render={(_: any, record: IReferCode) => (
                                            <Space size="middle">
                                                {ReferralCodeEdit && (
                                                <Button
                                                    onClick={() => showDrawer(record)} 
                                                    icon={<EditOutlined />} 
                                                    className="hover:!border-black hover:!text-black"
                                                />)}
                                            </Space>
                                        )}
                                    />
                                </Table>
                                <div>
                                    <Pagination
                                        currentPage={pageNumber.current}
                                        totalPages={totalPages}
                                        onPageChange={handlePageChange}
                                    />
                                </div>
                                
                            </TabPane>
                        ))
                    }
                </Tabs>
            </ConfigProvider>       
        </div>
    );
};

export default ReferCode;